import type { Query } from '@tanstack/react-query';
import type { APIError, GetArtifactQuery } from '@vizit/api';
import { pollWhilePending, useGetImageQuery } from '@vizit/api';
import { useGetArtifactQuery } from '@vizit/api';
import * as React from 'react';
import { AnalysisArtifacts } from '../..';

interface UseGetAssetDetailsImagesProps {
  /**
   * The ID of the organization to use for the original image.
   */
  organizationId: string;
  /**
   * The ID of the workspace to use for the original image.
   */
  workspaceId: string;
  /**
   * The ID of the asset to use for the original image.
   */
  assetId: string;
  /**
   * The function to call when the asset details fail to load
   *
   * @param error - The error that occurred
   */
  onAuthenticationError?: (error: unknown | APIError) => void;
  /**
   * Whether the five analysis-artifact queries should run. Artifacts are only
   * displayed on the Image Analysis tab; other tabs pass false so no artifact
   * GETs fire there (notably Spark Images, where generated images may never
   * have artifacts).
   */
  artifactsEnabled?: boolean;
}

/**
 * Shared query hook to get the original image, with additional query options for better UX.
 * Uses `useGetImageQuery` from `@vizit/api`.
 *
 * Updated options:
 * - `retry` - Retry 3 times on transient errors. Pending URL/score staleness
 *    is handled by `useGetImageQuery`'s own polling, not blind retry.
 * - `enabled` - Only enable the query if workspaceId and assetId are provided.
 * - `refetchOnWindowFocus` - This is needed if the data is stale.
 *    S3 has a limit of 1 hour with the token provided in the URL returned from the API.
 *
 * @param props - The props for the hook.
 * @returns The original image.
 */
function useGetOriginalImage(props: UseGetAssetDetailsImagesProps) {
  const { organizationId, workspaceId, assetId, onAuthenticationError } = props;

  const originalImageQuery = useGetImageQuery(
    {
      organization_id: organizationId,
      workspace_id: workspaceId,
      image_id: assetId,
    },
    {
      retry: import.meta.env.VITEST ? 0 : 3,
      enabled: !!organizationId && !!workspaceId && !!assetId,
      // Need this if data is stale. S3 has a limit of 1 hour with the token provided in the URL returned from the API.
      refetchOnWindowFocus: true,
    },
  );

  if (originalImageQuery.error) {
    onAuthenticationError?.(originalImageQuery.error);
  }

  return originalImageQuery;
}

/**
 * Shared query hook to get the original image and analysis artifacts, with additional query options for better UX.
 * Uses `useGetOriginalImage` and `useGetArtifactQuery` from `@vizit/api`.
 *
 * Updated options (applied to each of the five artifact queries):
 * - `retry` - Conditional: a 404 ("artifact not generated yet") never blind-retries, since
 *    that case is handled by the poll below; other, transient errors still get a few retries.
 * - `enabled` - Only enable the query if workspaceId and assetId are provided, the original
 *    image query is successful, and `artifactsEnabled` is true (see prop docs above).
 * - `refetchOnWindowFocus` - This is needed if the data is stale.
 *    S3 has a limit of 1 hour with the token provided in the URL returned from the API.
 * - `refetchInterval` - Poll-while-pending: keeps polling while the artifact 404s and the
 *    original image is still fresh enough that artifacts are plausibly generating, anchored
 *    on the original image's `updated_at ?? created_at`. Stops once the artifact resolves,
 *    the error isn't a 404, or the anchor falls outside the stale window.
 * - `refetchIntervalInBackground` - Keeps the above poll running while the tab is unfocused.
 *
 * @param props - The props for the hook.
 * @returns The analysis artifacts.
 */
function useGetAssetDetailsImages(props: UseGetAssetDetailsImagesProps) {
  const { organizationId, workspaceId, assetId, onAuthenticationError, artifactsEnabled = true } = props;

  const originalImage = useGetOriginalImage({ organizationId, workspaceId, assetId, onAuthenticationError });

  const artifactAnchor = originalImage.data?.updated_at ?? originalImage.data?.created_at ?? null;

  const artifactQueryOptions = React.useMemo(
    () => ({
      // A 404 means "artifact not generated yet" — handled by the poll below,
      // never by blind retry. Transient errors still get a few retries.
      retry: (failureCount: number, error: unknown) => {
        if (import.meta.env.VITEST) return false;
        if ((error as APIError).status === 404) return false;
        return failureCount < 3;
      },
      // Don't start querying until the original image is successful.
      enabled: !!organizationId && !!workspaceId && !!assetId && originalImage.isSuccess && artifactsEnabled,
      // Need this if data is stale. S3 has a limit of 1 hour with the token provided in the URL returned from the API.
      refetchOnWindowFocus: true,
      refetchIntervalInBackground: true,
      // Poll while the artifact 404s and the original image is fresh enough
      // that artifacts are plausibly still being generated (upload/rescore).
      refetchInterval: pollWhilePending((query: Query<GetArtifactQuery, APIError>) => {
        if (query.state.data) return [];
        if (query.state.error?.status !== 404) return [];
        return [artifactAnchor];
      }),
    }),
    [organizationId, workspaceId, assetId, originalImage.isSuccess, artifactsEnabled, artifactAnchor],
  );

  const attentionMap = useGetArtifactQuery(
    {
      organization_id: organizationId,
      workspace_id: workspaceId,
      image_id: assetId,
      artifact_type: 'attention',
    },
    artifactQueryOptions,
  );

  const gazeSequence = useGetArtifactQuery(
    {
      organization_id: organizationId,
      workspace_id: workspaceId,
      image_id: assetId,
      artifact_type: 'gaze',
    },
    artifactQueryOptions,
  );

  const visualAppealDrivers = useGetArtifactQuery(
    {
      organization_id: organizationId,
      workspace_id: workspaceId,
      image_id: assetId,
      artifact_type: 'drivers',
    },
    artifactQueryOptions,
  );

  const visualAppealDetractors = useGetArtifactQuery(
    {
      organization_id: organizationId,
      workspace_id: workspaceId,
      image_id: assetId,
      artifact_type: 'detractors',
    },
    artifactQueryOptions,
  );

  const focalPoints = useGetArtifactQuery(
    {
      organization_id: organizationId,
      workspace_id: workspaceId,
      image_id: assetId,
      artifact_type: 'focal_points',
    },
    artifactQueryOptions,
  );

  const hasArtifactsError = React.useMemo(() => {
    const errors = [
      attentionMap.error,
      gazeSequence.error,
      visualAppealDrivers.error,
      visualAppealDetractors.error,
      focalPoints.error,
    ];
    return errors.find((error) => error != null && (error as APIError).status !== 404) ?? null;
  }, [attentionMap, gazeSequence, visualAppealDrivers, visualAppealDetractors, focalPoints]);

  if (hasArtifactsError) {
    onAuthenticationError?.(hasArtifactsError);
  }

  const analysisItems = React.useMemo(
    () => ({
      [AnalysisArtifacts.OriginalImage]: {
        ...originalImage,
        data: originalImage.data
          ? {
              ...originalImage.data,
              thumbnailUrl: originalImage.data.url_360 ?? originalImage.data.url ?? undefined,
            }
          : originalImage.data,
      },
      [AnalysisArtifacts.AttentionMap]: attentionMap,
      [AnalysisArtifacts.GazeSequence]: gazeSequence,
      [AnalysisArtifacts.FocalPoints]: focalPoints,
      [AnalysisArtifacts.VisualAppealDrivers]: visualAppealDrivers,
      [AnalysisArtifacts.VisualAppealDetractors]: visualAppealDetractors,
    }),
    [originalImage, attentionMap, gazeSequence, visualAppealDrivers, visualAppealDetractors, focalPoints],
  );

  return {
    originalImage,
    analysisItems,
  };
}

export { useGetOriginalImage, useGetAssetDetailsImages };
